package top.wcpe.allincore.layout.engine

import kotlin.jvm.JvmWildcard

/**
 * 参与布局计算的节点抽象。
 * 上层组件树需实现此接口，仅暴露样式与子节点结构，与具体引擎实现解耦。
 */
interface LayoutNode {

    /** 返回节点本次布局所使用的样式。 */
    fun layoutStyle(): LayoutStyle

    /**
     * 返回节点的直接子节点列表，顺序决定布局先后。
     *
     * JVM 签名经 [JvmWildcard] 保持原 Java `List<? extends LayoutNode>` 形态，
     * 使既有 Java 实现类的协变返回（如 `List<TestNode>` 经通配符）零改动。
     */
    fun children(): List<@JvmWildcard LayoutNode>

    /**
     * 叶子节点的内禀尺寸测量器，可为 null（默认）。
     *
     * 若节点有 children，此方法会被忽略——仅对 children 为空的叶子生效。
     * 用于让 Text 这类按内容尺寸的元素在 `width=auto` 时不坍缩到 0。
     */
    fun measurer(): Measurer? {
        return null
    }

    /**
     * 是否参与布局（M6-f §8.2 visible）。默认 true。
     *
     * 布局引擎构建 Taffy 树时跳过 visible=false 的节点：其自身及其子树都不进入布局，
     * 等价于从父节点的 children 中临时摘除。渲染层 / 命中测试层应同步短路。
     */
    fun visible(): Boolean {
        return true
    }

    /**
     * 是否从父容器布局中分离（绝对浮层）。默认 false。
     *
     * 返回 true 时，节点自身及其子树仍会被布局（基于自身声明的 width / height
     * 作为独立 layout root），但不会被加到父容器的 flex / grid 排版中——典型场景
     * 是 modal、popup 等屏幕级浮层：它们由专门的渲染层（`ModalLayer` 等）
     * 居中或绝对定位，绝不应该挤压父 column 的其它子节点。
     *
     * visible=false 优先于本标记：不可见时整棵子树被跳过，自然不进入布局。
     */
    fun detachedFromParentLayout(): Boolean {
        return false
    }

    /**
     * 是否为「透明布局容器」（CSS `display: contents` 语义）。默认 false。
     *
     * 返回 true 时，若其父为 flex（[LayoutMode.ROW]/[LayoutMode.COLUMN]）容器，
     * 布局引擎**不**为本节点建独立布局盒，而是把它的子节点直接挂到父容器——使父的
     * `gap/align/justify/wrap` 直接作用于这些子节点（典型：$for/$cond 包裹循环/分支项的
     * 透明 box，让循环项像父的直接子一样参与主轴分布）。引擎在布局完成后给本节点合成一个
     * 偏移为 0、尺寸为「子节点包围范围」的盒，供渲染/命中坐标累加与内容高度测量使用。
     *
     * 仅在 flex 父下摊平；父为 [LayoutMode.STACK]/[LayoutMode.GRID]/
     * [LayoutMode.ABSOLUTE] 时按普通节点处理（那些模式下 justify 本无意义）。
     * [visible]=false 与 [detachedFromParentLayout] 优先于本标记。
     */
    fun transparentLayout(): Boolean {
        return false
    }

    /**
     * 本容器是否对其 [transparentLayout] 子节点执行 display: contents 摊平。默认 true。
     *
     * 滚动容器（viewport 固定、内容超出即滚动）需返回 false：它靠「直接子节点 flexShrink=0」
     * 保证内容可超出视窗滚动，若把透明容器（$for/$cond）摊平、循环项变成滚动容器的直接 flex 子，
     * 这些循环项会带默认 flexShrink=1 被压进视窗导致无法滚动。返回 false 时透明容器在该父下不摊平、
     * 仍作为受保护的直接子参与布局（justify 在滚动容器本无意义，故无损失）。
     */
    fun flattensTransparentChildren(): Boolean {
        return true
    }
}
